// Attaches a gallery video's <source src> only once the video is actually on screen.
//
// Why this exists: the PDP gallery renders its media twice, once in the pass CSS
// shows on mobile and once in the pass it shows on desktop. Videos carry filename
// alts rather than mobile_img / desktop_img, so neither pass filters them out and
// both copies shipped a real src with autoplay. display:none does not stop a
// <video autoplay> from downloading, so every PDP fetched each video twice. On
// level-serum that was 7.8 MB of the 15.7 MB of video on a cold desktop load.
//
// Sources now ship as data-lazy-src. A display:none element never intersects, so
// the hidden duplicate never attaches a src and never downloads. The visible copy
// attaches on first intersection, which for the hero video is immediately.
(() => {
  if (window.__galleryVideoLazy) return;
  window.__galleryVideoLazy = true;

  const SEL = 'video[data-lazy-video]';

  const attach = (video) => {
    if (video.dataset.lazyVideoAttached) return;
    video.dataset.lazyVideoAttached = '1';

    const sources = [...video.querySelectorAll('source[data-lazy-src]')];
    if (!sources.length) return;

    // Liquid ships every mp4 rendition smallest-first. Pick the smallest one
    // that still covers the rendered box (1.25x upscale is invisible for video;
    // bitrate, not pixels, dominates perceived quality). DPR capped at 2 and
    // the largest rendition is the fallback, so quality can only match today's.
    let chosen = sources[sources.length - 1];
    const box = video.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (sources.length > 1 && box.width > 2 && box.height > 2) {
      const needW = box.width * dpr;
      const needH = box.height * dpr;
      for (const source of sources) {
        const w = parseInt(source.dataset.videoWidth, 10);
        const h = parseInt(source.dataset.videoHeight, 10);
        if (w > 0 && h > 0 && w * 1.25 >= needW && h * 1.25 >= needH) {
          chosen = source;
          break;
        }
      }
    }
    const fallbackUrl = sources[sources.length - 1].dataset.lazySrc;
    chosen.setAttribute('src', chosen.dataset.lazySrc);
    chosen.removeAttribute('data-lazy-src');
    for (const source of sources) {
      if (source !== chosen) source.remove();
    }
    // Smaller rendition URLs are derived server-side from Shopify's naming
    // ladder; if one is ever missing, retry with the known-good largest.
    if (fallbackUrl && chosen.getAttribute('src') !== fallbackUrl) {
      chosen.addEventListener(
        'error',
        () => {
          chosen.setAttribute('src', fallbackUrl);
          video.load();
          if (video.autoplay) {
            const played = video.play();
            if (played && typeof played.catch === 'function') played.catch(() => {});
          }
        },
        { once: true }
      );
    }

    video.load();
    if (video.autoplay) {
      const played = video.play();
      // Autoplay can be refused (low power mode, user setting). The poster stays,
      // which is the same thing that happened before this file existed.
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }
  };

  // Very old browsers with no IntersectionObserver would otherwise never attach a
  // src at all, which would mean no video anywhere rather than a slower video. Fall
  // back to attaching anything with a real box. The hidden duplicate is display:none
  // and so measures 0x0, which is what keeps it out of this path too.
  if (!('IntersectionObserver' in window)) {
    const attachVisible = () => {
      for (const video of document.querySelectorAll(SEL)) {
        const box = video.getBoundingClientRect();
        if (box.width > 2 && box.height > 2) attach(video);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachVisible, { once: true });
    } else {
      attachVisible();
    }
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        attach(entry.target);
      }
    },
    // Start the fetch slightly before the video scrolls in so the first frame is
    // ready by the time it lands.
    { rootMargin: '200px' }
  );

  const watch = (video) => {
    if (video.dataset.lazyVideoWatched) return;
    video.dataset.lazyVideoWatched = '1';
    observer.observe(video);
  };

  const scan = (root) => {
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    if (root.matches && root.matches(SEL)) watch(root);
    if (root.querySelectorAll) {
      for (const video of root.querySelectorAll(SEL)) watch(video);
    }
  };

  const start = () => {
    scan(document);
    // The gallery swaps slides in on variant change and opens kit drawers after
    // load, so new videos can appear at any point.
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) scan(node);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
